import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import {
  fetchDirectAwardDetails,
  fetchNoticeDetails,
} from '../../services/publicTendersApi';
import { submitOfferRequest } from '../../services/cmsService';

export default function SolicitaOferta() {
  const { procedureId, utm_source } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState({
    loading: false,
    success: null,
    error: null,
  });
  const [procedureData, setProcedureData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [userData, setUserData] = useState(null);

  // SEO prerender setup
  useEffect(() => {
    if (typeof window !== 'undefined') window.prerenderReady = false;
  }, [procedureId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }, 10000);
    return () => clearTimeout(timeout);
  }, [procedureId]);

  useEffect(() => {
    if (!loadingData && (procedureData || userData === null)) {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }
  }, [loadingData, procedureData, userData]);

  // Validare parametri
  const validation = useMemo(() => {
    const errors = [];
    if (!procedureId) errors.push("Lipsește parametrul 'procedureId'.");
    if (procedureId && !/^[A-Za-z0-9_-]{3,40}$/.test(procedureId))
      errors.push("Parametrul 'procedureId' nu are un format valid.");

    return { isValid: errors.length === 0, errors };
  }, [procedureId]);

  // Verificare autentificare și extragere date user din token
  useEffect(() => {
    const token = Cookies.get('jwtToken');
    if (!token) {
      // Redirect la login cu return URL ca utilizatorul sa revina dupa autentificare
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const userName =
        decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
      const userEmail =
        decoded[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
        ];

      if (!userName || !userEmail) {
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
        return;
      }

      setUserData({
        name: userName,
        email: userEmail,
      });
    } catch (error) {
      console.error('Eroare la decodarea token-ului:', error);
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
    }
  }, [navigate]);

  // Redirect dacă parametrii nu sunt valizi
  useEffect(() => {
    if (!validation.isValid) {
      navigate('/abonamente');
    }
  }, [validation.isValid, navigate]);

  // Fetch procedure data
  useEffect(() => {
    const fetchProcedureData = async () => {
      if (!validation.isValid) {
        return;
      }

      setLoadingData(true);
      try {
        let data = null;
        const isDirectAward = procedureId.startsWith('ADV');

        if (isDirectAward) {
          // Pentru proceduri care încep cu ADV - folosesc DirectAwards
          try {
            data = await fetchDirectAwardDetails(procedureId);
            if (data && data.advForView) {
              setProcedureData({
                code: procedureId,
                name:
                  data.advForView.contractObject || 'Denumire nedisponibilă',
                value: data.advForView.estimatedValue
                  ? `${new Intl.NumberFormat('ro-RO').format(data.advForView.estimatedValue)} RON`
                  : 'Valoare nedisponibilă',
                authority:
                  data.advForView.contractAuthorityName ||
                  'Autoritate nedisponibilă',
                type: 'Atribuire Directă',
              });
            }
          } catch (error) {
            console.log('Eroare la încărcarea achizitiei directe:', error);
          }
        } else {
          // Pentru celelalte proceduri - folosesc Notices
          try {
            data = await fetchNoticeDetails(procedureId);
            if (data && data.stickyData && data.specificView) {
              setProcedureData({
                code: procedureId,
                name: data.stickyData.contractTitle || 'Denumire nedisponibilă',
                value: data.section21View?.estimatedValue
                  ? `${new Intl.NumberFormat('ro-RO').format(data.section21View.estimatedValue)} RON`
                  : 'Valoare nedisponibilă',
                authority: data.stickyData.caName || 'Autoritate nedisponibilă',
                type: data.specificView.noticeType || 'Procedură de achiziție',
              });
            }
          } catch (error) {
            console.log('Eroare la încărcarea procedurii de achiziție:', error);
          }
        }

        // Dacă nu găsește datele, redirect la abonamente
        if (!data) {
          console.error('Procedura nu a fost găsită în niciuna din surse');
          navigate('/abonamente');
          return;
        }
      } catch (error) {
        console.error('Eroare la încărcarea datelor procedurii:', error);
        navigate('/abonamente');
      } finally {
        setLoadingData(false);
      }
    };

    fetchProcedureData();
  }, [procedureId, validation.isValid, navigate]);

  useEffect(() => {
    document.title = `Solicită ofertă – ${procedureId || 'Licitatii-Constructii'}`;
  }, [procedureId]);

  // Return early cu loading dacă se încarcă datele
  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center px-4 py-10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă datele procedurii...</p>
        </div>
      </div>
    );
  }

  const handleConfirm = async () => {
    setStatus({ loading: true, success: null, error: null });

    try {
      // Validez datele înainte de trimitere
      if (!procedureId || !userData?.name || !userData?.email) {
        setStatus({
          loading: false,
          success: null,
          error: `Date incomplete pentru cerere. ProcedureId: ${procedureId}, Name: ${userData?.name}, Email: ${userData?.email}`,
        });
        return;
      }

      const requestData = {
        auctionId: String(procedureId), // Mă asigur că este string
        userName: userData.name || 'N/A',
        userEmail: userData.email || 'N/A',
        userRole: 'User',
        requestType: 'technical_offer',
        utm_source: 'email', // Pentru SolicitaOferta utm_source este "email"
      };

      const result = await submitOfferRequest(requestData);

      if (result.success) {
        setStatus({
          loading: false,
          success: `Cererea pentru ofertă tehnică a fost trimisă cu succes! ${result.requestId ? `ID Cerere: ${result.requestId}` : ''} ${result.estimatedDelivery ? `Livrare estimată: ${result.estimatedDelivery}` : 'Veți fi contactat în cel mult 24 de ore.'}`,
          error: null,
        });
      } else {
        setStatus({
          loading: false,
          success: null,
          error: `${result.message || 'A apărut o eroare la procesarea cererii.'} ${result.errorCode ? `Cod eroare: ${result.errorCode}` : ''} ${result.details || ''}`,
        });
      }
    } catch (error) {
      console.error('Eroare la trimiterea cererii:', error);
      setStatus({
        loading: false,
        success: null,
        error:
          'A apărut o eroare la trimiterea cererii. Vă rugăm să încercați din nou sau să ne contactați dacă problema persistă.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        {/* HEADER */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-extrabold tracking-tight text-indigo-700">
            Solicită generare ofertă tehnică
          </h1>
          <p className="text-gray-500 mt-2">
            Pentru procedura{' '}
            {procedureId ? (
              <span className="font-semibold text-gray-800">{procedureId}</span>
            ) : (
              '—'
            )}
          </p>
        </div>

        {/* ERORI */}
        {!validation.isValid && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-5 shadow-sm">
            <h2 className="font-semibold text-red-800 mb-2">
              Parametri invalizi
            </h2>
            <ul className="list-disc list-inside space-y-1">
              {validation.errors.map((e, idx) => (
                <li key={idx}>{e}</li>
              ))}
            </ul>
            <p className="text-sm text-red-600 mt-3">
              Te rugăm să verifici linkul sau să contactezi{' '}
              <a
                href="mailto:support@4projects.ro"
                className="underline text-red-700 font-medium"
              >
                support@4projects.ro
              </a>
              .
            </p>
          </div>
        )}

        {/* PANEL */}
        {validation.isValid && procedureData && userData && (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-xl p-6">
            {/* Procedură */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Cod procedură</span>
                <span className="text-sm font-mono bg-gray-100 text-gray-800 px-2 py-1 rounded">
                  {procedureData.code}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Tip procedură</span>
                <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                  {procedureData.type}
                </span>
              </div>

              <div>
                <span className="text-sm text-gray-500 uppercase">
                  Denumire
                </span>
                <div className="text-lg font-semibold text-gray-800">
                  {procedureData.name}
                </div>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-500 uppercase">
                  Autoritate
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  {procedureData.authority}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-500 uppercase">
                  Valoare estimată
                </span>
                <span className="text-base font-bold text-gray-900">
                  {procedureData.value}
                </span>
              </div>
            </div>

            {/* Informații utilizator */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Informații utilizator
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Nume</span>
                  <span className="text-sm font-medium text-gray-700">
                    {userData.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Email</span>
                  <span className="text-sm font-medium text-gray-700">
                    {userData.email}
                  </span>
                </div>
              </div>
            </div>

            {/* Mesaj */}
            <div className="mt-6 text-gray-600">
              Vom genera o ofertă personalizată pentru procedura de mai sus și o
              vei primi pe emailul <strong>{userData.email}</strong>.
            </div>

            {/* Rezultat */}
            {status.success && (
              <div className="mt-4 bg-green-50 border border-green-200 text-green-700 rounded-lg p-3">
                {status.success}
              </div>
            )}
            {status.error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">
                {status.error}
              </div>
            )}

            {/* Buton */}
            <div className="mt-6">
              <button
                onClick={handleConfirm}
                disabled={status.loading}
                className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow transition disabled:opacity-70"
              >
                {status.loading
                  ? 'Se procesează...'
                  : 'Confirmă solicitarea de generare de ofertă'}
              </button>
              <p className="mt-3 text-center text-xs text-gray-400">
                sursă: <span className="font-mono">{utm_source || 'n/a'}</span>
              </p>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="text-center text-gray-400 text-xs mt-6">
          © {new Date().getFullYear()} Licitatii-Constructii — Procurement made simple.
        </div>
      </div>
    </div>
  );
}
